import qs from "query-string";
import { toDbId, toObjectTypeAndId } from "common/helpers/globalId";
import { ApplicationEnvironment } from "common/constants/ApplicationContext";
import { type SingleManagerContextRouteParams } from "apps/portal/Portal/common/types";
import { type match } from "react-router-dom";
import type { ImportType, ImportStep } from "apps/staff/Staff/Import/ImportTakeover/types";
import type { ImportErrorType } from "apps/staff/Staff/Import/BulkImportErrorPage/constants";
import ID, { type Id } from "./ID";

// Dashboard
export const DASHBOARD_BASE_URL = "/portal";

// Edit stationery
export const EDIT_STATIONERY_URL = "/admin_settings/configuration_category/stationery";

// Edit email settings
export const EDIT_EMAIL_SETTINGS_URL =
  "/admin_settings/configuration_category/organization_settings";

// Edit asset report templates
export const editAssetReportTemplatesUrl = "/asset_reports";

// Subscriptions
export const SUBSCRIPTIONS_BASE_PATH = `${DASHBOARD_BASE_URL}/offering`;

export function getSubscriptionPath(dataRoomKey: string, step?: string) {
  return `${SUBSCRIPTIONS_BASE_PATH}/${ID(dataRoomKey) || ""}/subscribe/${step || ""}`;
}

export function getProspectCountersignPath() {
  return `/:arenaId/prospect/${ID(":subscriptionId")}/countersign`;
}
export function getProspectSubscriptionPath(prospectId: string) {
  return `/prospect/${toDbId(prospectId)}/subscription`;
}
export function getDocumentsMode() {
  return `${getSubscriptionPath(":dataRoomKey", "documents")}/:mode(print|docusign)`;
}

// CCTools
export const CCTOOLS_BASE_PATH = "/cctools";

export function getCCToolsPath() {
  return CCTOOLS_BASE_PATH;
}

export function getGPArenasPath(arenaId?: Id) {
  return `${CCTOOLS_BASE_PATH}/arenas/${ID(arenaId) || ""}`;
}

export function getSessionsPath() {
  return `${CCTOOLS_BASE_PATH}/sessions/`;
}

export function getUsersPath() {
  return `${CCTOOLS_BASE_PATH}/users/`;
}

export function getCCLogsPath() {
  return `${CCTOOLS_BASE_PATH}/cclogs/`;
}

export const getToolsPath = () => `${CCTOOLS_BASE_PATH}/tools/`;

export function getFeatureFlagsPath() {
  return `${CCTOOLS_BASE_PATH}/featureflags/`;
}

// Investment Entities & Accounts
export const INVESTMENT_ENTITIES_BASE_PATH = "/entities";

export function getTouPath(touType: string) {
  return `/tou/${touType}`;
}

export function getInvestmentEntitiesPath(investmentEntityId?: Id) {
  return `${INVESTMENT_ENTITIES_BASE_PATH}/${ID(investmentEntityId) || ""}`;
}

export function getInvestmentBulkReviewPath() {
  return `/review_transactions`;
}

export function getAccountPath(accountId?: Id) {
  return `/account/${ID(accountId) || ""}`;
}

// LP Portal
export const LP_PORTAL_BASE_PATH = "/lp";

export function getSingleManagerPortalPath(arenaDomain: string) {
  return `/i/${arenaDomain}`;
}

export function getLpPortalPath() {
  return `${LP_PORTAL_BASE_PATH}/`;
}

export function getLpPortalDashboardPath() {
  return getLpPortalPath();
}

export function getLpPortalLoginPath() {
  return `${LP_PORTAL_BASE_PATH}/login`;
}

export function getLpPortalHomepagePath() {
  return `${LP_PORTAL_BASE_PATH}/home`;
}

export function getLpPortalInvestmentsPath(arenaDomain?: string) {
  return `${
    arenaDomain ? getSingleManagerPortalPath(arenaDomain) : LP_PORTAL_BASE_PATH
  }/investments`;
}

export function getLpPortalDocumentsPath() {
  return `${LP_PORTAL_BASE_PATH}/documents`;
}

export function getLpPortalTaxDocumentsPath() {
  return `${LP_PORTAL_BASE_PATH}/documents/tax`;
}

export function getLpPortalEmailsPath(arenaDomain?: string) {
  return `${
    arenaDomain ? getSingleManagerPortalPath(arenaDomain) : LP_PORTAL_BASE_PATH
  }/emails`;
}

export function getLpPortalOfferingsPath(arenaDomain?: string) {
  return `${
    arenaDomain ? getSingleManagerPortalPath(arenaDomain) : LP_PORTAL_BASE_PATH
  }/offerings`;
}

export function getLpPortalOrgSettingsPath() {
  return `${LP_PORTAL_BASE_PATH}/organization_settings`;
}

export function getLpPortalUpdateTwoFaPath() {
  return `${LP_PORTAL_BASE_PATH}/two_step_auth_phone`;
}

export function getLpPortalConfirmNewTwoFaPath() {
  return `${LP_PORTAL_BASE_PATH}/two_step_auth_confirm`;
}

export function getLpPortalUpdateEmailPath() {
  return `${LP_PORTAL_BASE_PATH}/email_address_update`;
}

export function getLpPortalConfirmNewEmailPath() {
  return `${LP_PORTAL_BASE_PATH}/email_address_confirm`;
}

export function getLpPortalNewLinkedAccountsPath() {
  return `${LP_PORTAL_BASE_PATH}/new_linked_accounts`;
}
export function getLpPortalOfferingPath(offeringId: Id) {
  return `${LP_PORTAL_BASE_PATH}/offering/${ID(offeringId)}`;
}

export function getNpPortalPositionPath(
  positionId: Id,
  section?: string,
  arenaDomain?: string,
) {
  return `${
    arenaDomain ? getSingleManagerPortalPath(arenaDomain) : LP_PORTAL_BASE_PATH
  }/position/${ID(positionId)}${section ? `/${section}` : ""}`;
}

export function getPortal2dot0PositionPath(positionId: Id, arenaDomain?: string) {
  return `/i/${arenaDomain}/position/${positionId}/overview`;
}

export function getLpPortalManagerPath(arenaId: Id) {
  return `${getLpPortalInvestmentsPath()}?manager=${ID(arenaId)}`;
}

export function getLpPortalPositionDocumentsPath(positionId: Id, arenaDomain?: string) {
  return getNpPortalPositionPath(positionId, "documents", arenaDomain);
}

export function getLpPortalDocumentPreviewPath(documentId: Id) {
  return `${LP_PORTAL_BASE_PATH}/documents/preview/${documentId}`;
}
export function getLpPortalPositionDocumentPreviewPath(
  positionId: Id,
  documentId: Id,
  arenaDomain?: string,
) {
  return `${getLpPortalPositionDocumentsPath(positionId, arenaDomain)}/preview/${documentId}`;
}

export function getLpPortalPositionTransactionsPath(positionId: Id, arenaDomain?: string) {
  return getNpPortalPositionPath(positionId, "transactions", arenaDomain);
}

export function getLpPortalPositionOverviewPath(positionId: Id, arenaDomain?: string) {
  return getNpPortalPositionPath(positionId, "overview", arenaDomain);
}

export function getLpPortalAccountActivationPath() {
  return `${LP_PORTAL_BASE_PATH}/join`;
}

export function getLpPortalTermsAndPrivacyPath() {
  return `${LP_PORTAL_BASE_PATH}/terms-and-privacy/jsq`;
}

export function getLpPortalTermsAndPrivacyPathNoNav(arenaDomain?: string) {
  return `${
    arenaDomain ? getSingleManagerPortalPath(arenaDomain) : LP_PORTAL_BASE_PATH
  }/terms-and-privacy-nn/jsq`;
}

// Account activation landing page for unauthenticated users (portal SPA)
export function getLpPortalAccountActivationPathNew() {
  return "/networked_portal/join";
}

export function getLpPortalTouBasePath() {
  return "/networked_portal/legal/terms-of-use";
}

export function getUnifiedPortalPP(code?: string) {
  const activationCode = code ? `?activation=${code}` : "";
  return `/networked_portal/terms-and-privacy/jsq/pp${activationCode}`;
}

export function getUnifiedPortalToU(code?: string) {
  const activationCode = code ? `?activation=${code}` : "";
  return `/networked_portal/terms-and-privacy/jsq/tou${activationCode}`;
}

export function getUnifiedPortalTouBasePath() {
  return "/networked_portal/terms-and-privacy/jsq";
}

export function getUnifiedPortalTouActivation(code?: string) {
  const activationCode = code ? `?activation=${code}` : "";
  return `${getUnifiedPortalTouBasePath()}${activationCode}`;
}

export function getUnifiedPortalTouManagerPath(managerName: string, code?: string) {
  const activationCode = code ? `&activation=${code}` : "";
  return `${getUnifiedPortalTouBasePath()}?manager=${encodeURIComponent(
    managerName,
  )}${activationCode}`;
}

export const getTouBasePath = (code?: string) => {
  return code && code.length > 0 ? getUnifiedPortalTouBasePath() : "/lp/terms-and-privacy/jsq";
};

export const getTouLink = (origin: string, code: string) => {
  const basePath = getTouBasePath(code);
  return `${origin}${basePath}/tou`;
};

export const getTouLinkWithActivationCode = (origin: string, code: string) => {
  return getTouLink(origin, code) + (code.length > 0 ? `?activation=${code}` : "");
};

type AssetsPathArgs = {
  entityId?: Id;
  assetId?: Id;
  positionId?: Id;
  childEntityId?: Id;
  section?: string;
};

const PREFIXES = new Map([
  [ApplicationEnvironment.Staff, ""],
  [ApplicationEnvironment.SinglePortal, "/portal/investment"],
  [ApplicationEnvironment.GlobalPortal, "/lp/position"],
]);

/**
 * If the passed value is an global id, convert to db id, else return val
 * @param val
 */
export function toDb(val: string | number | undefined): string | number | undefined {
  if (typeof val === "undefined") {
    return undefined;
  }

  if (
    (typeof val === "number" && Number.isInteger(val)) ||
    Number.isInteger(parseInt(val as string, 10)) ||
    (typeof val === "string" && val.startsWith(":"))
  ) {
    return val;
  }

  try {
    if (typeof val === "string") {
      if (
        ["AssetObjectType", "PositionObjectType", "AccountObjectType"].some((str) =>
          atob(val).includes(`${str}:`),
        )
      ) {
        return toDbId(val);
      }
      return val;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/**
 * Calculates any path in the position/entities tab, based on the given parameters.
 * Notes:
 * The logic is based from getAssetsPath function below but the decision was made to split it
 * into a separate function for now to allow for more specific use case handlings the entities/positions
 * tabs.
 *
 * If positionId is present, entityId is not needed, unless a child entity is present
 * childEntityId, if present indicates a sub-entity, different from the top level entity.
 *
 * @param args
 * @param env
 * @param prefixOverride
 */
export function getEntitiesOrPositionPath(
  args: AssetsPathArgs,
  env: ApplicationEnvironment,
  prefixOverride?: string,
) {
  const prefix = prefixOverride ?? PREFIXES.get(env);
  const [positionId_, entityId_, childEntityId_] = [
    toDb(args.positionId),
    toDb(args.entityId),
    toDb(args.childEntityId),
    toDb(args.assetId || ""),
  ];
  const positionId = positionId_ ? `/${positionId_}` : "";
  const entityId = !positionId_ && entityId_ ? `/${entityId_}` : "";
  const childEntityId =
    childEntityId_ && childEntityId_ !== entityId_ ? `/${childEntityId_}` : "";
  const section = args.section ? `/${args.section}` : "";
  const entitySeparator = entityId || childEntityId ? "/account" : "";
  const entityOrPositionPath = `${prefix}${positionId}${entitySeparator}${entityId}${childEntityId}${section}`;
  return entityOrPositionPath;
}

/**
 * Calculates any path in the assets tab, based on the given parameters.
 * Notes:
 * `/asset` indicates a specific asset
 * if positionId is present, entityId is not needed, unless a child entity is present
 * childEntityId, if present indicates a sub-entity, different from the top level entity.
 *
 * @param args
 * @param env
 * @param arenaDomain if provided, paths generated will be for portal 2.0
 */
export function getAssetsPath(
  args: AssetsPathArgs,
  env: ApplicationEnvironment,
  arenaDomain?: string,
) {
  const prefix = arenaDomain ? `/i/${arenaDomain}/position` : PREFIXES.get(env);
  const [positionId_, entityId_, childEntityId_, assetId_] = [
    toDb(args.positionId),
    toDb(args.entityId),
    toDb(args.childEntityId),
    toDb(args.assetId || ""),
  ];
  const positionId = positionId_ ? `/${positionId_}` : "";
  const entityId = !positionId_ && entityId_ ? `/${entityId_}` : "";
  const childEntityId =
    childEntityId_ && childEntityId_ !== entityId_ ? `/${childEntityId_}` : "";
  const assetId = assetId_ ? `/${assetId_}` : "";
  const section = args.section ? `/${args.section}` : "";
  const assetsSeparator = assetId ? "/asset" : "/assets";
  const entitySeparator = env === ApplicationEnvironment.Staff ? "/account" : "";
  const assetPath = `${prefix}${positionId}${entitySeparator}${entityId}${childEntityId}${assetsSeparator}${assetId}${section}`;
  return assetPath;
}

export function getAssetsPortalRouteOrPath(
  args: AssetsPathArgs,
  env: ApplicationEnvironment,
  routeParams: match<SingleManagerContextRouteParams>,
  isForRouting: boolean,
  arenaDomain?: string,
) {
  const path = isForRouting ? `:arenaDomain` : (routeParams.params.arenaDomain ?? "");
  return getAssetsPath(args, env, arenaDomain ? path : undefined);
}

// Quarterly Data Upload
export const QUARTERLY_DATA_UPLOAD_BASE_PATH = "/investor-reporting";

export function getQuarterlyDataUploadPath() {
  return `${QUARTERLY_DATA_UPLOAD_BASE_PATH}/`;
}

export function getGpLiteTermsPath() {
  return `${QUARTERLY_DATA_UPLOAD_BASE_PATH}/legal/networked-portal-terms-of-use`;
}

// map object types to urls:

const OBJECT_TYPE_TO_URL_GETTER: Record<string, (id: Id, secondaryId?: Id) => string> = {
  OfferingObjectType: getLpPortalOfferingPath,
  PositionObjectType: (positionId, secondaryId) =>
    getNpPortalPositionPath(positionId, secondaryId?.toString()),
  AssetObjectType: (assetId, positionId) =>
    getAssetsPath({ positionId, assetId }, ApplicationEnvironment.GlobalPortal),
  ArenaObjectType: getLpPortalManagerPath,
  // to fill in as paths corresponding to searchable items are built
};

export function getRouteFromGlobalId(
  globalId: string,
  secondaryGlobalId?: string,
): string | null {
  const [objectType, id] = toObjectTypeAndId(globalId);
  const secondaryId = secondaryGlobalId ? toObjectTypeAndId(secondaryGlobalId)[1] : undefined;
  const urlGetter = OBJECT_TYPE_TO_URL_GETTER[objectType];
  return urlGetter ? urlGetter(id, secondaryId) : null;
}

// Staff
export function getStaffDashboardPath() {
  return "/dashboard";
}

export function getStaffAssistedDashboardPath() {
  return "/assisted_dashboard";
}

export function getStaffReportingPath(entityGroupId: string) {
  return `/dashboard/reporting/${entityGroupId}`;
}

export function getStaffInvestmentEntitiesPath() {
  return "/entities";
}

export function getStaffImportAssetsTemplateWizardPath() {
  return "/import_assets/template";
}

export function getStaffImportsAndExportsPath() {
  return "/imports_and_exports";
}

export function getStaffDownloadTemplateFilePath(downloadConfig: Record<string, unknown>) {
  const params = qs.stringify(downloadConfig);

  return `/import/asset_import_template.xlsx?${params}`;
}

export function getStaffUploadAssetsPath() {
  return "/import_assets";
}

export function getStaffUploadAssetsStepOnePath() {
  return "/import_assets/step1";
}

export function getStaffUploadAssetsStepTwoPath() {
  return "/import_assets/step2";
}

export function getStaffUploadAssetsStepThreePath() {
  return "/import_assets/step3";
}

export function getStaffUploadEntitiesPath() {
  return "/import_entities";
}

export function getStaffUploadEntitiesStepOnePath() {
  return "/import_entities/step1";
}

export function getStaffUploadEntitiesStepTwoPath() {
  return "/import_entities/step2";
}

export function getStaffUploadEntitiesStepThreePath() {
  return "/import_entities/step3";
}

export function getStaffCrossEntityTransactionImportPath(transactionType: string): string {
  return transactionType.length ? `/transaction_upload/${transactionType}_import` : "";
}

export function getStaffYardiTransactionImportPath() {
  return "/yardi_im/upload";
}

export function getStaffBulkImportPath() {
  return "/import/dashboard";
}

export function getStaffBulkImportTypePath(importType: ImportType) {
  return `/import/${importType}`;
}

export function getStaffBulkImportTypeStepPath(importType: ImportType, step: ImportStep) {
  return `/import/${importType}/${step}`;
}

export function getStaffImportDataPath(importType: ImportType, jobId: string) {
  return `/import/${importType}/importData/${jobId}`;
}

export function getStaffImportSummaryPath(importType: ImportType, jobId: string) {
  return `/import/${importType}/summary/${jobId}`;
}

export function getStaffImportFieldDescriptionsPath() {
  return "/import/field_descriptions";
}

export function getStaffBulkImportErrorPath(errorType?: ImportErrorType) {
  const basePath = "/import/error";
  if (errorType) {
    return `${basePath}?type=${errorType}`;
  }
  return basePath;
}

export function getStaffAssetsTabPath() {
  return "/assets";
}

export function getStaffAccountingTabPath() {
  return ["/account/:entityId/accounting", "/account/:entityId/accounting/soi"];
}

export function getReportTemplateSettingsPath() {
  return "/report_template_settings";
}

export function getCustomReportWorkflowPath(publicationType: string, templateId: string) {
  return `/publication/custom_report/${publicationType}/${templateId}`;
}

export function getCustomReportEditPath(publicationId: Id) {
  return `/publication/custom_report/${ID(publicationId)}`;
}

export function getCustomReportEntitiesApiPath() {
  return "/publication/custom_report/entities";
}

export const BILLING_CENTER_BASE_PATH = "/billing-center";

export function getBillingCenterPath() {
  return BILLING_CENTER_BASE_PATH;
}

export function getBillingCenterEntityPath(entityId: number, entityName: string) {
  return `${BILLING_CENTER_BASE_PATH}/${entityId}?name=${encodeURIComponent(entityName)}`;
}

export const ACCESS_ROLE_BASE_PATH = "/admin_settings/portal_accounts/access_roles";

export function getEditRoleBase() {
  return `${ACCESS_ROLE_BASE_PATH}/edit_role`;
}

export function getEditRolePath(roleId: Id) {
  return `${getEditRoleBase()}/${ID(roleId)}`;
}

export function getManagerNameFromPath(path?: string) {
  if (!path) {
    return "";
  }

  const currentRoute = path.split("/");
  let currentManagerName = "";
  /* currentRoute looks like ["", "i", "rockstonecap", misc.] for single manager authentication */

  if (currentRoute.length >= 2) {
    if (currentRoute[1] === "i" && currentRoute.length >= 3) {
      /* Single-manager context */
      // eslint-disable-next-line prefer-destructuring
      currentManagerName = currentRoute[2];
    }
  }
  return currentManagerName;
}

export function getReportingDashboardsPath() {
  return "/dashboards/:id?";
}

export function getNewDashboardPath() {
  return "/dashboards/new";
}
